<template>
  <div class="admin-management">
    <h2>👥 Admin Management</h2>
    
    <div class="stats-summary">
      <div class="stat-box">
        <h4>{{ adminList.length }}</h4>
        <p>Total Admins</p>
      </div>
      <div class="stat-box">
        <h4>{{ hardcodedAdmins.length }}</h4>
        <p>Hardcoded</p>
      </div>
      <div class="stat-box">
        <h4>{{ firestoreAdmins.length }}</h4>
        <p>Firestore</p>
      </div>
    </div>

    <!-- Add New Admin -->
    <div class="add-admin-section">
      <h3>➕ Add New Admin</h3>
      <div class="add-admin-form">
        <input 
          v-model="newAdminUid" 
          type="text" 
          placeholder="Enter User ID (UID)"
          @keyup.enter="addAdmin"
        />
        <button @click="addAdmin" :disabled="!newAdminUid.trim() || loading">
          {{ loading ? 'Adding...' : 'Add Admin' }}
        </button>
      </div>
      <p class="hint">💡 Tip: Get the UID from the Users page</p>
    </div>

    <!-- Current Admins List -->
    <div class="admins-list-section">
      <h3>📋 Current Admins</h3>
      
      <div v-if="loadingAdmins" class="loading">Loading admins...</div>
      
      <div v-else-if="adminList.length === 0" class="empty">
        No admins found
      </div>
      
      <div v-else class="admins-table-wrapper">
        <table class="admins-table">
          <thead>
            <tr>
              <th>UID</th>
              <th>Email</th>
              <th>Name</th>
              <th>Source</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="admin in adminList" :key="admin.uid">
              <td>
                <code class="uid-code">{{ admin.uid.substring(0, 12) }}...</code>
              </td>
              <td>{{ admin.email || 'N/A' }}</td>
              <td>{{ admin.name || 'N/A' }}</td>
              <td>
                <span class="source-badge" :class="admin.source">
                  {{ admin.source === 'hardcoded' ? '🔒 Hardcoded' : '☁️ Firestore' }}
                </span>
              </td>
              <td>
                <button 
                  v-if="admin.source === 'firestore'" 
                  @click="removeAdmin(admin.uid)"
                  class="remove-btn"
                  :disabled="loading"
                >
                  Remove
                </button>
                <span v-else class="protected-badge">Protected</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase/init';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { showSuccess, showError, showWarning, showConfirm, showToast } from '@/utils/alerts';

export default {
  name: 'AdminManagement',
  data() {
    return {
      adminList: [],
      hardcodedAdmins: ['6KAyZ5CrB3cNJzQdbrgRIUy57EV2'], // From userServices.js
      firestoreAdmins: [],
      newAdminUid: '',
      loading: false,
      loadingAdmins: false,
      usersCache: {}
    };
  },
  methods: {
    async fetchAdmins() {
      this.loadingAdmins = true;
      try {
        // Get Firestore admins
        const adminRef = doc(db, 'admin', 'admins');
        const snap = await getDoc(adminRef);
        
        if (snap.exists()) {
          this.firestoreAdmins = snap.data().uids || [];
        } else {
          this.firestoreAdmins = [];
        }

        // Fetch user details for all admins
        await this.fetchUserDetails();
        
        // Combine hardcoded and Firestore admins
        this.adminList = [
          ...this.hardcodedAdmins.map(uid => ({
            uid,
            source: 'hardcoded',
            email: this.usersCache[uid]?.email || 'N/A',
            name: this.usersCache[uid]?.name || 'N/A'
          })),
          ...this.firestoreAdmins.map(uid => ({
            uid,
            source: 'firestore',
            email: this.usersCache[uid]?.email || 'N/A',
            name: this.usersCache[uid]?.name || 'N/A'
          }))
        ];
      } catch (e) {
        console.error('Error fetching admins:', e);
        showError('Failed to load admin list. Please try again.');
      } finally {
        this.loadingAdmins = false;
      }
    },
    
    async fetchUserDetails() {
      // Get all unique UIDs
      const allUids = [...new Set([...this.hardcodedAdmins, ...this.firestoreAdmins])];
      
      // Fetch user details from Firestore
      for (const uid of allUids) {
        try {
          const userRef = doc(db, 'users', uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            this.usersCache[uid] = {
              email: userData.mail || userData.email || 'N/A',
              name: userData.nom || userData.name || userData.displayName || 'N/A'
            };
          }
        } catch (e) {
          console.error(`Error fetching user ${uid}:`, e);
        }
      }
    },
    
    async addAdmin() {
      const uid = this.newAdminUid.trim();
      
      if (!uid) {
        showWarning('Please enter a valid UID');
        return;
      }
      
      // Check if already admin
      if (this.hardcodedAdmins.includes(uid) || this.firestoreAdmins.includes(uid)) {
        showWarning('This user is already an admin');
        return;
      }
      
      this.loading = true;
      try {
        // Add to Firestore admin list
        const adminRef = doc(db, 'admin', 'admins');
        const snap = await getDoc(adminRef);
        
        let currentAdmins = [];
        if (snap.exists()) {
          currentAdmins = snap.data().uids || [];
        }
        
        currentAdmins.push(uid);
        
        await setDoc(adminRef, {
          uids: currentAdmins,
          updatedAt: Date.now()
        });
        
        showSuccess(`Successfully added admin: ${uid.substring(0, 12)}...`);
        this.newAdminUid = '';
        await this.fetchAdmins();
      } catch (e) {
        console.error('Error adding admin:', e);
        showError('Failed to add admin. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    
    async removeAdmin(uid) {
      const result = await showConfirm(
        `Remove admin access for ${uid.substring(0, 12)}...?`,
        'Remove Admin'
      );
      
      if (!result.isConfirmed) {
        return;
      }
      
      this.loading = true;
      try {
        const adminRef = doc(db, 'admin', 'admins');
        const snap = await getDoc(adminRef);
        
        if (snap.exists()) {
          let currentAdmins = snap.data().uids || [];
          currentAdmins = currentAdmins.filter(id => id !== uid);
          
          await setDoc(adminRef, {
            uids: currentAdmins,
            updatedAt: Date.now()
          });
          
          showSuccess(`Successfully removed admin: ${uid.substring(0, 12)}...`);
          await this.fetchAdmins();
        }
      } catch (e) {
        console.error('Error removing admin:', e);
        showError('Failed to remove admin. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.fetchAdmins();
  }
};
</script>

<style scoped>
.admin-management {
  padding: 20px;
  max-width: 1200px;
}

h2 {
  color: #e37267;
  margin-bottom: 20px;
  font-size: 28px;
}

h3 {
  color: #333;
  margin-bottom: 16px;
  font-size: 20px;
}

/* Stats Summary */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.stat-box {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-box h4 {
  font-size: 36px;
  margin: 0 0 8px 0;
  font-weight: 700;
}

.stat-box p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

/* Add Admin Section */
.add-admin-section {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 32px;
}

.add-admin-form {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.add-admin-form input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.add-admin-form input:focus {
  outline: none;
  border-color: #667eea;
}

.add-admin-form button {
  padding: 12px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.add-admin-form button:hover:not(:disabled) {
  background: #5568d3;
}

.add-admin-form button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hint {
  color: #6b7280;
  font-size: 13px;
  margin: 0;
}

/* Admins List Section */
.admins-list-section {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.loading, .empty {
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 16px;
}

.admins-table-wrapper {
  overflow-x: auto;
}

.admins-table {
  width: 100%;
  border-collapse: collapse;
}

.admins-table thead {
  background: #f9fafb;
}

.admins-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
}

.admins-table td {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  color: #111827;
}

.admins-table tbody tr:hover {
  background: #f9fafb;
}

.uid-code {
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #374151;
}

.source-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.source-badge.hardcoded {
  background: #fef3c7;
  color: #92400e;
}

.source-badge.firestore {
  background: #dbeafe;
  color: #1e40af;
}

.remove-btn {
  padding: 6px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.remove-btn:hover:not(:disabled) {
  background: #dc2626;
}

.remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.protected-badge {
  display: inline-block;
  padding: 4px 12px;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* Messages */
.message {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  animation: slideIn 0.3s ease-out;
  z-index: 1000;
}

.message.success {
  background: #10b981;
  color: white;
}

.message.error {
  background: #ef4444;
  color: white;
}

@keyframes slideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
